import { getApiBase } from './api-base';
import { getSessionCookie, getToken } from './auth-token';
import { HttpResponseError } from './http-error';

export type HttpOptions = RequestInit & NextFetchRequestConfig;
export type HttpOptionsWithoutBody = Omit<HttpOptions, 'body'>;

export const api = async <TResponse>(path: string, options: HttpOptions): Promise<TResponse> => {
  const serverCookieToken = await getSessionCookie();
  const firebaseToken = await getToken();
  const isBodyFormData = options.body instanceof FormData;
  const isDevelopment = process.env.NODE_ENV === 'development';
  const shouldSetCookieHeader = !!(!isDevelopment && serverCookieToken);
  const shouldSetFirebaseTokenHeader = !!(isDevelopment && firebaseToken);

  let headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (options.body && isBodyFormData) {
    headers = options.headers ?? {};
  }

  if (shouldSetFirebaseTokenHeader) {
    headers = {
      authorization: `Bearer ${firebaseToken}`,
      ...headers,
    };
  }

  if (shouldSetCookieHeader) {
    headers = {
      cookie: `session=${serverCookieToken}`,
      ...headers,
    };
  }

  const response = await fetch(`${await getApiBase()}${path}`, {
    credentials: isDevelopment ? undefined : 'include',
    ...options,
    headers,
  });

  if (response.ok) {
    const text = await response.text();

    if (text === '') {
      return {} as TResponse;
    }

    return JSON.parse(text) as TResponse;
  }

  const httpErr = new HttpResponseError(
    response,
    `[${options.method}] ${path}: ${response.status} - ${response.statusText}`,
  );

  await httpErr.loadBody();
  throw httpErr;
};

async function createRequest<TResponse>(
  method: string,
  url: string,
  options?: HttpOptionsWithoutBody,
  body?: unknown,
): Promise<TResponse> {
  const opts: HttpOptions = { method, ...options };
  const isBodyFormData = body instanceof FormData;

  if (body && isBodyFormData) {
    opts.body = body;
  } else if (body) {
    opts.body = JSON.stringify(body);
  }

  return api<TResponse>(url, opts);
}

export const http = {
  get: <TResponseGet>(url: string, options?: HttpOptionsWithoutBody) =>
    createRequest<TResponseGet>('GET', url, options),

  delete: <TResponseGet = void>(url: string, options?: HttpOptionsWithoutBody) =>
    createRequest<TResponseGet>('DELETE', url, options),

  post: <TResponse = void>(url: string, body: unknown, options?: HttpOptionsWithoutBody) =>
    createRequest<TResponse>('POST', url, options, body),

  patch: <TResponse = void>(url: string, body: unknown, options?: HttpOptionsWithoutBody) =>
    createRequest<TResponse>('PATCH', url, options, body),

  put: <TResponse = void>(url: string, body: unknown, options?: HttpOptionsWithoutBody) =>
    createRequest<TResponse>('PUT', url, options, body),
};
